import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'antd'
import { EditOutlined, DownOutlined, CalendarOutlined, LoadingOutlined, CheckOutlined, WarningOutlined, LockOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { SaveStatus, useSaveStatus } from '../../../../components/inline-edit/use-save-status'
import IntlMessages from '../../../../util/IntlMessages'

export { SelectEditor, DateEditor, TextEditor } from '../../../../components/inline-edit/editors'
export type { ISelectEditorProps, IDateEditorProps, ITextEditorProps } from '../../../../components/inline-edit/editors'

interface IEditingContextValue {
  editingCell: string | null
  setEditingCell: (key: string | null) => void
}

export const UsersTableEditingContext = createContext<IEditingContextValue>({
  editingCell: null,
  setEditingCell: () => undefined,
})

export const UsersTableEditingProvider = ({ children, suspended }: { children: React.ReactNode; suspended?: boolean }): React.ReactElement => {
  const [editingCell, setEditingCellState] = useState<string | null>(null)
  useEffect(() => {
    if (suspended) {
      setEditingCellState(null)
    }
  }, [suspended])
  const setEditingCell = (key: string | null) => {
    if (suspended) {
      return
    }
    setEditingCellState(key)
  }
  return <UsersTableEditingContext.Provider value={{ editingCell, setEditingCell }}>{children}</UsersTableEditingContext.Provider>
}

type CellStatus = SaveStatus
type CellAffordance = 'pencil' | 'chevron' | 'calendar'

export function emptyCellDisplay(editable: boolean, fieldLabelId: string): React.ReactElement {
  if (!editable) {
    return <IntlMessages id="app.notSet" />
  }
  return (
    <span className="vt-users-cell-placeholder">
      <IntlMessages id="users.table.addValue" values={{ field: <IntlMessages id={fieldLabelId} /> }} />
    </span>
  )
}

const AFFORDANCE_ICONS: Record<CellAffordance, React.ReactNode> = {
  pencil: <EditOutlined />,
  chevron: <DownOutlined />,
  calendar: <CalendarOutlined />,
}

export interface ICellShellProps {
  display: React.ReactNode
  affordance?: CellAffordance
  lockReason?: string
  status?: CellStatus
  errorMessage?: string
  align?: 'end'
  onActivate?: () => void
  /** Focus the cell as it appears, for a cell coming back from its editor. */
  focusOnMount?: boolean
}

export const CellShell = (props: ICellShellProps): React.ReactElement => {
  const { display, affordance = 'pencil', lockReason, status = 'idle', errorMessage, align, onActivate, focusOnMount } = props
  const { formatMessage } = useIntl()
  const shellRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (focusOnMount) {
      shellRef.current?.focus()
    }
  }, [])
  const alignClass = align === 'end' ? ' vt-users-cell--end' : ''

  if (lockReason) {
    return (
      <div className={`vt-users-cell vt-users-cell--locked${alignClass}`}>
        <span className="vt-users-cell-value">{display}</span>
        <span className="vt-users-cell-affordance">
          <Tooltip title={formatMessage({ id: lockReason })}>
            <LockOutlined />
          </Tooltip>
        </span>
      </div>
    )
  }

  if (!onActivate) {
    return <div className={`vt-users-cell${alignClass}`}>{display}</div>
  }

  const activate = () => {
    if (status !== 'saving') {
      onActivate()
    }
  }

  return (
    <div
      ref={shellRef}
      className={`vt-users-cell vt-users-cell--editable vt-users-cell--${status}${alignClass}`}
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          activate()
        }
      }}
    >
      <span className="vt-users-cell-value">{display}</span>
      <span className="vt-users-cell-affordance">
        {status === 'saving' && <LoadingOutlined />}
        {status === 'saved' && <CheckOutlined className="vt-users-cell-check" />}
        {status === 'error' && (
          <Tooltip title={errorMessage}>
            <WarningOutlined className="vt-users-cell-warning" />
          </Tooltip>
        )}
        {status === 'idle' && AFFORDANCE_ICONS[affordance]}
      </span>
    </div>
  )
}

export interface IEditableCellProps<TValue> {
  cellKey: string
  editable: boolean
  affordance: CellAffordance
  display: React.ReactNode
  renderEditor: (actions: { commit: (value: TValue) => void; cancel: () => void }) => React.ReactNode
  onCommit: (value: TValue) => Promise<void>
  lockReason?: string
}

export function EditableCell<TValue>(props: IEditableCellProps<TValue>): React.ReactElement {
  const { cellKey, editable, affordance, display, renderEditor, onCommit, lockReason } = props
  const { editingCell, setEditingCell } = useContext(UsersTableEditingContext)
  const { status, errorMessage, runSave } = useSaveStatus()
  // Set by Escape, read when the editor closes: only a cancel takes focus back to the cell. A blur
  // that closes the editor leaves focus wherever the person clicked.
  const escaped = useRef(false)

  if (!editable) {
    return <CellShell display={display} lockReason={lockReason} />
  }

  if (editingCell === cellKey) {
    escaped.current = false
    const cancel = () => setEditingCell(null)
    const commit = async (value: TValue) => {
      escaped.current = false
      setEditingCell(null)
      await runSave(() => onCommit(value))
    }
    return (
      <div
        className="vt-users-cell vt-users-cell--editing"
        onKeyDownCapture={(event) => {
          escaped.current = event.key === 'Escape'
        }}
      >
        {renderEditor({ commit, cancel })}
      </div>
    )
  }

  return (
    <CellShell display={display} affordance={affordance} status={status} errorMessage={errorMessage} onActivate={() => setEditingCell(cellKey)} focusOnMount={escaped.current} />
  )
}
